import {
  Text,
  VStack,
  Grid,
  GridItem,
  Box,
  Stepper,
  Step,
  StepIndicator,
  StepTitle,
  StepDescription,
  Button,
  HStack
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, TimeIcon, WarningIcon, NotAllowedIcon } from "@chakra-ui/icons";
import ConciliationDetailsItems from "./ConciliatonDetailsItems";
import { ConciliacaoStatus, EtapaConciliacaoStatus, PropostaFinanceira } from "@/models/consignado/propostaFinanceira.model";
import { useState, useMemo } from "react";
import { conciliarPropostaFinanceira, getPropostaFinanceira, getRelatoriosPorProposta } from "@/services/consignado/conciliation.service";
import { ConfiguracaoConciliacao } from "@/models/conciliation-config.model";
import ConciliationDetailsModal from "./ConciliationDetailsModal";

const normalizeConciliationStepName = (value: string) => value.trim().toLocaleLowerCase("pt-BR");

export interface ConciliationAvaliacao {
  regraId: string;
  operador: string;
  descricao: string;
  resultado: boolean;
  valorOrigem: number | null;
  valorDestino: number | null;
  rotulo_origem?: string;
  rotulo_destino?: string;
}

export interface ConciliationData {
  title: string;
  status: string;
  observacao: string;
  avaliacoes: ConciliationAvaliacao[];
  source1Name?: string;
  source2Name?: string;
}

interface SelectedProposalFallback {
  id: string;
  shorthash?: string;
  valor_liberado?: number;
  data_proposta?: string;
  numero_ccb?: string;
}

interface ConciliationDetailsProps {
  proposalId: string;
  isShowSteps: boolean;
  selectedProposal?: SelectedProposalFallback;
  configuracoes: ConfiguracaoConciliacao[];
  onConciliationUpdated?: () => Promise<void> | void;
}

export default function ConciliationDetails({
  proposalId,
  isShowSteps,
  configuracoes,
  onConciliationUpdated,
}: ConciliationDetailsProps) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConciliation, setSelectedConciliation] = useState<ConciliationData | null>(null);

  const {
    data: propostaFinanceira,
    isLoading: isPropostaFinanceiraLoading,
  } = useQuery<PropostaFinanceira>({
    queryKey: ["consig-conciliacao-proposta", proposalId],
    queryFn: async () => getPropostaFinanceira(proposalId),
    enabled: Boolean(proposalId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: false,
  });

  const {
    data: relatorios = [],
  } = useQuery({
    queryKey: ["consig-conciliacao-relatorios", proposalId],
    queryFn: async () => getRelatoriosPorProposta(proposalId),
    enabled: Boolean(proposalId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: false,
  });

  const conciliarMutation = useMutation({
    mutationFn: async () => conciliarPropostaFinanceira(proposalId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["consig-conciliacao-relatorios", proposalId] }),
        queryClient.invalidateQueries({ queryKey: ["consig-conciliacao-proposta", proposalId] }),
      ]);
    },
  });

  const dynamicSteps = useMemo(() => {
    if (!configuracoes) return [];

    return [...configuracoes]
      .sort((a, b) => a.ordem - b.ordem)
      .filter((step) => {
        const relatorio = relatorios?.find((item) => {
          return normalizeConciliationStepName(item.tipo) === normalizeConciliationStepName(step.nome);
        });

        const etapaConciliacao = propostaFinanceira?.etapasConciliacao?.find((item) => {
          return normalizeConciliationStepName(item.etapa) === normalizeConciliationStepName(step.nome);
        });

        if (relatorio || etapaConciliacao)
          return true;

        const requiresInsurance =
          step.condicaoSeguro === "APENAS_COM_SEGURO" 

        const hasInsurance = !!propostaFinanceira?.insuranceCompanyId;

        if (requiresInsurance && !hasInsurance) {
          return false
        }

        return true
      })
      .map((config) => {
        const relatorio = relatorios?.find((item) => {
          return normalizeConciliationStepName(item.tipo) === normalizeConciliationStepName(config.nome);
        });

        const etapaConciliacao = propostaFinanceira?.etapasConciliacao?.find((item) => {
          return normalizeConciliationStepName(item.etapa) === normalizeConciliationStepName(config.nome);
        });

        if (!relatorio && !etapaConciliacao) {
          return {
            title: config.nome,
            status: ConciliacaoStatus.PENDENTE,
            rawConfig: config,
            relatorio: null,
            etapaConciliacao: null,
          };
        }

        return {
          title: config.nome,
          status: (relatorio?.status ?? etapaConciliacao?.status ?? ConciliacaoStatus.PENDENTE) as ConciliacaoStatus,
          rawConfig: config,
          relatorio: relatorio ?? null,
          etapaConciliacao: etapaConciliacao ?? null,
        };
      });
  }, [configuracoes, propostaFinanceira?.etapasConciliacao, propostaFinanceira?.insuranceCompanyId, relatorios]);

  const handleOpenModal = (stepData: typeof dynamicSteps[0]) => {
    const avaliacoesBackEnd = stepData.relatorio?.detalhes?.avaliacoes || [];
    const regras = stepData.rawConfig.configuracao.regras || [];

    // Enriquecer cada avaliação com o rótulo da regra correspondente (por índice)
    const avaliacoesComLabels = avaliacoesBackEnd.map((avaliacao: ConciliationAvaliacao, index: number) => {
      const regra = regras[index];
      return {
        ...avaliacao,
        rotulo_origem: regra?.origem?.rotulo || regra?.origem?.campo || null,
        rotulo_destino: regra?.destino?.rotulo || regra?.destino?.campo || null,
      };
    });

    const data: ConciliationData = {
      title: stepData.title,
      status: stepData.relatorio?.status || stepData.etapaConciliacao?.status || "PENDENTE",
      observacao: stepData.relatorio?.observacao || stepData.etapaConciliacao?.observacao || "Aguardando conciliação...",
      avaliacoes: avaliacoesComLabels as ConciliationAvaliacao[],
      source1Name: "Origem",
      source2Name: "Destino",
    };

    setSelectedConciliation(data);
    setIsModalOpen(true);
  };

  const handleConciliateClick = async () => {
    try {
      await conciliarMutation.mutateAsync();
      await onConciliationUpdated?.();
    } catch (error) {
      console.error("Erro ao conciliar proposta:", error);
    }
  };

  const isSuccess = (status: string) => status === ConciliacaoStatus.CONCLUIDO;

  const hasStepDetails = (relatorio: unknown, etapaConciliacao: EtapaConciliacaoStatus | null) => {
    return Boolean(relatorio || etapaConciliacao);
  };

  const resolveStepUI = (status: ConciliacaoStatus) => {
    switch (status) {
      case ConciliacaoStatus.CONCLUIDO:
        return {
          color: "green.500",
          icon: <CheckIcon color="white" />,
          label: "Conciliado"
        }

      case ConciliacaoStatus.DIVERGENTE:
        return {
          color: "red.500",
          icon: <WarningIcon color="white" />,
          label: "Divergência"
        }

      case ConciliacaoStatus.PENDENTE:
        return {
          color: "yellow.500",
          icon: <TimeIcon color="white" />,
          label: "Aguardando relatório"
        }

      case ConciliacaoStatus.NAO_SE_APLICA:
        return {
          color: "gray.500",
          icon: <NotAllowedIcon color="white" />,
          label: "Não se aplica"
        }

      default:
        return {
          color: "gray.400",
          icon: <TimeIcon color="white" />,
          label: ""
        }
    }
  }

  return (
   <>
    {(propostaFinanceira && propostaFinanceira.id) && (
      <VStack
        spacing={6}
        align="stretch"
        borderRadius="lg"
        p={6}
        borderWidth="2px"
        borderColor="gray.200"
      >
        <Grid templateColumns={isShowSteps ? "repeat(12, 1fr)" : ""} gap={6} width="100%">
          <GridItem colSpan={isShowSteps ? 9 : 12} width="100%">
            <ConciliationDetailsItems proposal={propostaFinanceira} relatorios={relatorios} handleConciliateClick={handleConciliateClick} isLoading={conciliarMutation.isPending} dynamicSteps={dynamicSteps} />
          </GridItem>

          {isShowSteps && (
            <GridItem colSpan={3}>
              {dynamicSteps.length > 0 ? ( 
                <Box maxH="590px" overflowY="auto" pr={2}> 
                  <Stepper
                    orientation="vertical"
                    gap="0"
                    width='100%'
                    position='relative'
                    index={dynamicSteps.length}
                  >
                    {dynamicSteps
                      .map((step, index) => {
                        const isLastStep = index === dynamicSteps.length - 1
                        const ui = resolveStepUI(step.status)

                        return (
                          <Step key={index}>
                            <StepIndicator          
                              sx={{
                                bg: `${ui.color} !important`,
                                borderColor: `${ui.color} !important`,
                                color: "white"
                              }}
                            >
                              {ui.icon}
                            </StepIndicator>

                            <Box pb={isLastStep ? 0 : 4}>
                              <StepTitle>{step.title}</StepTitle>
                              <StepDescription>
                                <HStack spacing={1}>
                                  <Text 
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color={
                                      step.status === ConciliacaoStatus.DIVERGENTE ? "red.500" : 
                                      step.status === ConciliacaoStatus.PENDENTE ? "yellow.500" : 
                                      step.status === ConciliacaoStatus.NAO_SE_APLICA ? "gray.500" :
                                      isSuccess(step.status) ? "green.500" : "blue.400"
                                    }
                                  >
                                    {ui.label}
                                  </Text>
                                </HStack>
                              </StepDescription>
                              
                              <Button
                                size="xs"
                                mt={2}
                                variant="outline"
                                isDisabled={!hasStepDetails(step.relatorio, step.etapaConciliacao)}
                                onClick={() => handleOpenModal(step)}
                              >
                                Ver detalhes
                              </Button>
                            </Box>

                            {!isLastStep && 
                              <Box
                                position="absolute"
                                height="calc(100% - 3.35rem)"
                                width="2px"
                                bg={ui.color}
                                left=".93rem"
                                top="10"
                                bottom="10"
                              />
                            }
                          </Step>
                        );
                      })}
                  </Stepper>
                </Box>
              ) : (
                <Box p={4} textAlign="center">
                  <Text fontSize="sm" color="gray.500">
                    {!propostaFinanceira ? "Carregando dados..." : "Nenhuma configuração encontrada."}
                  </Text>
                </Box>
              )}
            </GridItem>
          )}
        </Grid>

        <ConciliationDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          conciliationData={selectedConciliation}
        />
      </VStack>
    )}

    {(!proposalId && !propostaFinanceira && !isPropostaFinanceiraLoading) && (
      <Box
        height={200}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Text
          fontSize={20}        
        >
          Proposta não encontrada
        </Text>
      </Box>
    )}
  </>
  );
}